import { inject, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { catchError, combineLatest, map, merge, mergeMap, of, race, switchMap, take, takeUntil, tap } from 'rxjs';
import { SortDirection } from '../../../core/enums/sort-direction';
import { PAGE_IDS } from '../../../core/router/page-ids';
import { AppInsightsService } from '../../../core/services/app-insights/app-insights.service';
import { clearAllState, startTourIfUnseen } from '../../../core/store/actions/app.actions';
import { getUtcDate } from '../../../shared/functions/date.functions';
import { closeOnEscape } from '../../../shared/functions/dialog.functions';
import { TourType } from '../../../tours/data/tour-config';
import { patientSwitchInitiated, setPatientIdSuccess } from '../../../user/store/actions/user.actions';
import { selectPatientId } from '../../../user/store/selectors/user.selectors';
import { ScheduleAppointmentDialogComponent } from '../../components/schedule/schedule-appointment-dialog/schedule-appointment-dialog.component';
import { AppointmentSearchRequest } from '../../models/appointment';
import { AppointmentsService } from '../../services/appointments/appointments.service';
import { ScheduleAppointmentHelperService } from '../../services/schedule-appointment-helper/schedule-appointment-helper.service';
import {
  closeScheduleAppointmentDialog,
  getNextPatientAppointment,
  getNextPatientAppointmentFailure,
  getNextPatientAppointmentSuccess,
  getPastPatientAppointmentsFailure,
  getPastPatientAppointmentsSuccess,
  getPatientAppointments,
  getUpcomingPatientAppointmentsFailure,
  getUpcomingPatientAppointmentsSuccess,
  loadMorePastAppointments,
  openScheduleAppointmentDialog,
  scheduleAppointmentDialogNextStep,
} from '../actions/appointments.actions';
import { selectPageNumber, selectSortColumn } from '../selectors/appointments.selector';

@Injectable()
export class AppointmentEffects {
  private actions$ = inject(Actions);
  private appointmentsService = inject(AppointmentsService);
  private scheduleAppointmentHelperService = inject(ScheduleAppointmentHelperService);
  private appInsightsService = inject(AppInsightsService);
  private store = inject(Store);
  private dialog = inject(MatDialog);
  private readonly cancelRequest$ = this.actions$.pipe(ofType(patientSwitchInitiated, clearAllState));

  getNextPatientAppointment$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(getNextPatientAppointment, setPatientIdSuccess),
      concatLatestFrom(() => [this.store.select(selectPatientId)]),
      mergeMap(([, patientId]) => {
        return this.appointmentsService.getNextPatientAppointment(patientId).pipe(
          takeUntil(this.cancelRequest$),
          map(patientAppointment => {
            return getNextPatientAppointmentSuccess({ patientAppointment });
          }),
          catchError(err => {
            return of(getNextPatientAppointmentFailure({ error: err.error?.errors }));
          })
        );
      })
    );
  });

  getUpcomingPatientAppointments$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(getPatientAppointments),
      concatLatestFrom(() => [this.store.select(selectPatientId), this.store.select(selectSortColumn)]),
      mergeMap(([, patientId, sortColumn]) => {
        const appointmentSearchRequest: AppointmentSearchRequest = {
          sortColumn,
          scheduledAfterUtc: getUtcDate(),
          sortDirection: SortDirection.Ascending,
          includeAll: true,
        };
        return this.appointmentsService.getUpcomingPatientAppointments(patientId, appointmentSearchRequest).pipe(
          takeUntil(this.cancelRequest$),
          map(patientAppointments => {
            return getUpcomingPatientAppointmentsSuccess({ patientAppointments });
          }),
          catchError(err => {
            return of(getUpcomingPatientAppointmentsFailure({ error: err.error?.errors }));
          })
        );
      })
    );
  });

  getPastPatientAppointments$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(getPatientAppointments, loadMorePastAppointments),
      concatLatestFrom(() => [
        this.store.select(selectPatientId),
        this.store.select(selectPageNumber),
        this.store.select(selectSortColumn),
      ]),
      mergeMap(([, patientId, pageNumber, sortColumn]) => {
        const appointmentSearchRequest: AppointmentSearchRequest = {
          pageNumber,
          sortColumn,
          scheduledBeforeUtc: getUtcDate(),
          pageSize: 5,
          sortDirection: SortDirection.Descending,
          includeAll: false,
        };
        return this.appointmentsService.getPastPatientAppointments(patientId, appointmentSearchRequest).pipe(
          takeUntil(this.cancelRequest$),
          map(response => {
            let isAtEndOfData = false;
            const totalRecordCount = response.totalRecordCount;
            let currentPage = pageNumber || 1;
            let patientAppointments = response.result;
            const requestedPageSize = 5;

            if (patientAppointments == null || patientAppointments.length == 0) {
              patientAppointments = [];
              currentPage--;
              isAtEndOfData = true;
            }

            const loadedCount = Math.max((currentPage - 1) * requestedPageSize, 0) + patientAppointments.length;
            const reachedTotal = totalRecordCount > 0 && loadedCount >= totalRecordCount;
            const shortPage = patientAppointments.length > 0 && patientAppointments.length < requestedPageSize;
            if (reachedTotal || shortPage) isAtEndOfData = true;

            return getPastPatientAppointmentsSuccess({
              patientAppointments,
              pageNumber: currentPage <= 0 ? 1 : currentPage,
              isAtEndOfData,
              totalRecordCount,
            });
          }),
          catchError(err => {
            return of(getPastPatientAppointmentsFailure({ error: err.error?.errors }));
          })
        );
      })
    );
  });

  startAppointmentsTourIfUnseen$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(getPastPatientAppointmentsSuccess),
      map(() => {
        return startTourIfUnseen({ tourType: TourType.Appointments });
      })
    );
  });

  openScheduleAppointmentDialog$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(openScheduleAppointmentDialog),
        tap(() => {
          const dialogRef = this.dialog.open(ScheduleAppointmentDialogComponent, {
            id: 'scheduleAppointmentDialog',
            disableClose: true,
            autoFocus: false,
            width: '560px',
            height: '600px',
            panelClass: 'schedule-appt-dialog',
          });
          // Route Escape through the dialog's own Cancel handler so a dirty appointment form still
          // hits the unsaved-changes confirmation instead of being force-closed.
          closeOnEscape(dialogRef, { onEscape: () => dialogRef.componentInstance.cancel() });
        })
      );
    },
    { dispatch: false }
  );

  closeScheduleAppointmentDialog$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(closeScheduleAppointmentDialog),
        tap(() => {
          this.dialog.getDialogById('scheduleAppointmentDialog')?.close();
        })
      );
    },
    { dispatch: false }
  );

  scheduleAppointmentDialogNextStep$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(scheduleAppointmentDialogNextStep),
        tap(() => {
          this.scheduleAppointmentHelperService.nextStep();
        })
      );
    },
    { dispatch: false }
  );

  startUxTimer$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(getPatientAppointments),
        tap(() => {
          this.appInsightsService.startUxTimer(PAGE_IDS.APPOINTMENTS);
        })
      );
    },
    { dispatch: false }
  );

  stopUxTimer$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(getPatientAppointments),
        switchMap(() => {
          const bothSuccess$ = combineLatest([
            this.actions$.pipe(ofType(getUpcomingPatientAppointmentsSuccess), take(1)),
            this.actions$.pipe(ofType(getPastPatientAppointmentsSuccess), take(1)),
          ]).pipe(map(() => ({ status: 'success' as const })));

          const anyFailure$ = merge(
            this.actions$.pipe(ofType(getUpcomingPatientAppointmentsFailure), take(1)),
            this.actions$.pipe(ofType(getPastPatientAppointmentsFailure), take(1))
          ).pipe(map(a => ({ status: 'failure' as const, error: a.error })));

          return race(bothSuccess$, anyFailure$).pipe(
            tap(result => {
              if (result.status === 'success') {
                this.appInsightsService.stopUxTimer(PAGE_IDS.APPOINTMENTS, { status: 'success' });
              } else {
                const code = (result.error as { status?: number } | undefined)?.status ?? 0;
                this.appInsightsService.stopUxTimer(PAGE_IDS.APPOINTMENTS, {
                  status: 'failure',
                  statusClass: code === 0 ? 'network' : `${Math.floor(code / 100)}xx`,
                  errorCode: code,
                });
              }
            })
          );
        })
      );
    },
    { dispatch: false }
  );
}
